// src/pages/extensions/index.jsx
import React, { useEffect, useState } from 'react';
import { fetchData, save } from '../../services/stacks.js';
import { Alert, Snackbar } from '@mui/material';
import UserExtensions from './userExtensions.jsx';
import InterfaceExtensions from './interfaceExtensions.jsx';
import { authenticatedUserAddress, getUserData } from '../../services/auth.js';
import { clientNetwork, devPrincipal, devStoragePath, interfaceExtensionsFileName, sentimentExtensionApi, userExtensionsFileName } from '../../lib/constants.js';
import RegisteredExtensions from './registeredExtensions.jsx';
import axios from 'axios';
import './style.scss';

const Extensions = () => {
  const [userExtensions, setUserExtensions] = useState([]);
  const [filters, setFilters] = useState({
    'send-message': false,
    'like-message': false,
    'dislike-message': false,
    'favorable-reply-message': false,
    'unfavorable-reply-message': false,
    'gift-message': false,
  });
  const [loading, setLoading] = useState(true); // Added loading state
  const [openSnackBar, setOpenSnackBar] = useState(false);
  const [errorMsg, setErrorMsg] = useState({ severity: '', msg: '' });

  const [myExtListReadable, setMyExtListReadable] = useState(true);

  const authedUser = authenticatedUserAddress({ network: clientNetwork });
  const [activeTab, setActiveTab] = useState(
    authedUser === devPrincipal ? 'registered' : 'extensions'
  );

  const [registeredExtensions, setRegisteredExtensions] = useState([]);
  const [page, setPage] = useState(0);
  const [recomendedExtensions, setRecomendedExtension] = useState([]);
  const [interfaceExtensions, setInterfaceExtensions] = useState([]);

  // Function to handle filter changes
  const handleFilterChange = (op) => {
    setFilters({ ...filters, [op]: !filters[op] });
  };

  // Function to filter extensions based on active filters
  const filterExtensions = (extensions) => {
    const filteredExtensions = extensions.filter((extension) => {
      const filtered = Object.keys(filters).every((op) => {
        if (!filters[op]) return true;
        if (!extension?.ops) return false;
        const opEntry = extension.ops[op];
        return opEntry;
      });
      return filtered;
    });
    return filteredExtensions;
  };

  useEffect(() => {
    const initExtensionPage = async () => {
      let userExtension, interfaceExtensions;
      const recomendedExtensions = await axios.get(sentimentExtensionApi).then(res => res.data).catch(res => []);
      userExtension = await fetchData(userExtensionsFileName, { decrypt: false, verify: false });
      interfaceExtensions = await axios.get(devStoragePath + interfaceExtensionsFileName).then(res => res.data).catch(err => []);
      if (userExtension?.success) {
        userExtension = userExtension.success ? userExtension.result : [];
        setRecomendedExtension(recomendedExtensions);
        setUserExtensions(userExtension);
        setInterfaceExtensions(interfaceExtensions);
        setMyExtListReadable(true);
      } else {
        if (userExtension?.error?.code === "does_not_exist") setMyExtListReadable(false);
        setUserExtensions([]);
      }
      setLoading(false);
    };
    initExtensionPage();
  }, []);

  return (
    <>
      <div className="extensions-page">
        <div className="ext-tabs">
          {authedUser === devPrincipal && (
            <button
              className={`ext-tab-button ${activeTab === 'registered' ? 'active' : ''}`}
              onClick={() => setActiveTab('registered')}
            >
              Registered
            </button>
          )}

          <button
            className={`ext-tab-button ${activeTab === 'extensions' ? 'active' : ''}`}
            onClick={() => setActiveTab('extensions')}
          >
            Extensions
          </button>

          <button
            className={`ext-tab-button ${activeTab === 'myExtensions' ? 'active' : ''}`}
            onClick={() => setActiveTab('myExtensions')}
          >
            My Extensions
          </button>
        </div>

        {/* Filtering options */}
        <div className="filter-controls">
          {Object.keys(filters).map((op) => (
            <label key={op} className="filter-label">
              <input
                type="checkbox"
                checked={filters[op]}
                onChange={() => handleFilterChange(op)}
              />
              {op.replace(/-/g, ' ')}
            </label>
          ))}
        </div>

        <div className="ext-tab-content">
          {activeTab === 'registered' && <RegisteredExtensions
            registeredExtensions={registeredExtensions}
            setRegisteredExtensions={setRegisteredExtensions}
            setInterfaceExtensions={setInterfaceExtensions}
            page={page}
            setPage={setPage}
            recomendedExtensions={recomendedExtensions}
            interfaceExtensions={interfaceExtensions}
            setErrorMsg={setErrorMsg}
            setOpenSnackBar={setOpenSnackBar}
          />}

          {(activeTab === 'extensions' && myExtListReadable) && (
            <InterfaceExtensions
              filterExtensions={filterExtensions}
              userExtensions={userExtensions}
              setUserExtensions={setUserExtensions}
              filters={filters}
              interfaceExtensions={interfaceExtensions}
              setInterfaceExtensions={setInterfaceExtensions}
              setErrorMsg={setErrorMsg}
              setOpenSnackBar={setOpenSnackBar}
            />
          )}

          {(activeTab === 'myExtensions' && myExtListReadable && authedUser !== devPrincipal) && (
            <UserExtensions
              userExtensionsFileName={userExtensionsFileName}
              filterExtensions={filterExtensions}
              userExtensions={userExtensions}
              setUserExtensions={setUserExtensions}
              filters={filters}
              setOpenSnackBar={setOpenSnackBar}
              setErrorMsg={setErrorMsg}
            />
          )}

          {!myExtListReadable && (<Alert severity="info" sx={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>{"Your extensions list is being prepared, if this message persists reauthenticate."}</Alert>)}

          {(loading && myExtListReadable) && <div className="loading">Loading extensions...</div>}
        </div>
      </div>

      {/* Snack bar alert */}
      <Snackbar
        open={openSnackBar}
        autoHideDuration={6000}
        onClose={() => setOpenSnackBar(false)}
      >
        <Alert
          onClose={() => setOpenSnackBar(false)}
          severity={errorMsg.severity}
          sx={{ width: '100%' }}
        >
          {errorMsg.msg}
        </Alert>
      </Snackbar>
    </>
  );
};

export default Extensions;